// src/pages/dealer/DealerPayments.jsx
// Shows all dealer payments: bidding passes, listing credits, car bid payments

import React, { useEffect, useState } from 'react';
import { dealerAPI } from '../../services/dealerAPI';

const fmt = (n) => n ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

const StatusBadge = ({ status }) => {
  const map = {
    success:   { bg: '#dcfce7', color: '#166534', label: '✅ Success'  },
    initiated: { bg: '#dbeafe', color: '#1d4ed8', label: '⏳ Pending'  },
    failed:    { bg: '#fee2e2', color: '#991b1b', label: '❌ Failed'   },
    pending:   { bg: '#fef9c3', color: '#854d0e', label: '🕐 Pending'  },
  };
  const s = map[status] || { bg: '#f3f4f6', color: '#374151', label: status };
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
};

const TABS = [
  { key: 'passes',  label: '🔨 Bidding Passes',    color: '#7c3aed' },
  { key: 'credits', label: '🏷️ Listing Credits',   color: '#0057b7' },
  { key: 'bids',    label: '🚗 Car Bid Payments',  color: '#059669' },
];

export default function DealerPayments() {
  const [activeTab, setActiveTab] = useState('passes');
  const [passes,    setPasses]    = useState([]);
  const [credits,   setCredits]   = useState([]);
  const [bids,      setBids]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [summary,   setSummary]   = useState({ passes: 0, credits: 0, bids: 0 });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dealerAPI.getPassPurchaseHistory(),
      dealerAPI.getCreditPurchaseHistory(),
      dealerAPI.getMyOffers(),
    ])
      .then(([pr, cr, or]) => {
        const passData   = pr.data?.data   || [];
        const creditData = cr.data?.data   || [];
        const offerData  = (or.data?.data  || []).filter(o => o.payment_status === 'success' || o.payment_status === 'paid');

        setPasses(passData);
        setCredits(creditData);
        setBids(offerData);

        setSummary({
          passes:  passData.filter(p => p.status === 'success').reduce((s, p) => s + Number(p.amount || 0), 0),
          credits: creditData.filter(c => c.status === 'success').reduce((s, c) => s + Number(c.amount || 0), 0),
          bids:    offerData.reduce((s, o) => s + Number(o.offer_price || 0), 0),
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#003566', margin: '0 0 4px' }}>
          💳 My Payments
        </h1>
        <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>All your payment history across bidding, credits, and car purchases</p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        <SummaryCard icon="🔨" label="Bidding Pass Spend"    value={fmt(summary.passes)}  bg="#ede9fe" color="#7c3aed" count={passes.filter(p=>p.status==='success').length}  unit="passes"  />
        <SummaryCard icon="🏷️" label="Listing Credit Spend" value={fmt(summary.credits)} bg="#dbeafe" color="#0057b7" count={credits.filter(c=>c.status==='success').length} unit="purchases" />
        <SummaryCard icon="🚗" label="Car Bid Payments"      value={fmt(summary.bids)}    bg="#dcfce7" color="#059669" count={bids.length}                                   unit="cars paid" />
        <SummaryCard icon="💰" label="Total Spent"           value={fmt(summary.passes + summary.credits + summary.bids)} bg="#fff7ed" color="#c2410c" count={null} unit="" highlight />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f3f4f6', borderRadius: 12, padding: 4 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              flex: 1, padding: '10px 8px',
              background: activeTab === t.key ? '#fff' : 'transparent',
              border: 'none', borderRadius: 9,
              fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: activeTab === t.key ? 700 : 500,
              color: activeTab === t.key ? t.color : '#6b7280',
              cursor: 'pointer',
              boxShadow: activeTab === t.key ? '0 1px 6px rgba(0,0,0,0.10)' : 'none',
              transition: 'all 0.18s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ width: 40, height: 40, border: '4px solid #e0e0e0', borderTopColor: '#003566', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
        </div>
      ) : (
        <>
          {activeTab === 'passes'  && <PassesTable  data={passes}  />}
          {activeTab === 'credits' && <CreditsTable data={credits} />}
          {activeTab === 'bids'    && <BidsTable    data={bids}    />}
        </>
      )}
    </div>
  );
}

/* ── Summary Card ── */
function SummaryCard({ icon, label, value, bg, color, count, unit, highlight }) {
  return (
    <div style={{
      background: highlight ? `linear-gradient(135deg, #003566, #001d3d)` : '#fff',
      borderRadius: 14, padding: '20px',
      boxShadow: highlight ? '0 4px 20px rgba(0,53,102,0.2)' : '0 2px 10px rgba(0,53,102,0.07)',
      border: highlight ? '2px solid #FFD60A' : '1px solid #e5e7eb',
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: highlight ? 'rgba(255,214,10,0.15)' : bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 11, color: highlight ? 'rgba(255,255,255,0.6)' : '#9ca3af', fontWeight: 600, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Playfair Display', serif", color: highlight ? '#FFD60A' : color }}>{value}</div>
        {count !== null && (
          <div style={{ fontSize: 11, color: highlight ? 'rgba(255,255,255,0.5)' : '#9ca3af', marginTop: 2 }}>{count} {unit}</div>
        )}
      </div>
    </div>
  );
}

/* ── Table Wrapper ── */
function TableWrap({ children, empty }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,53,102,0.07)', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
      {empty ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <p style={{ fontWeight: 600, color: '#6b7280', margin: '0 0 4px' }}>No records found</p>
          <p style={{ fontSize: 13 }}>Your payment history will appear here.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>{children}</div>
      )}
    </div>
  );
}

const TH = ({ children, right }) => (
  <th style={{ padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.8, textAlign: right ? 'right' : 'left', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
    {children}
  </th>
);
const TD = ({ children, right, bold }) => (
  <td style={{ padding: '14px 16px', fontSize: 13, color: bold ? '#003566' : '#374151', fontWeight: bold ? 700 : 400, textAlign: right ? 'right' : 'left', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' }}>
    {children}
  </td>
);

/* ── Bidding Passes Table ── */
function PassesTable({ data }) {
  const PASS_LABELS = { day: '1 Day Pass ⚡', week: 'Weekly Pass 🔥', month: 'Monthly Pass 👑' };
  return (
    <TableWrap empty={data.length === 0}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <TH>#</TH>
            <TH>Pass Type</TH>
            <TH>Duration</TH>
            <TH>Amount</TH>
            <TH>Status</TH>
            <TH>Razorpay Order</TH>
            <TH>Expires At</TH>
            <TH>Purchased On</TH>
          </tr>
        </thead>
        <tbody>
          {data.map((p, i) => (
            <tr key={p.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
              <TD>{i + 1}</TD>
              <TD bold>{PASS_LABELS[p.pass_type] || p.pass_type}</TD>
              <TD>{p.duration_days} day{p.duration_days > 1 ? 's' : ''}</TD>
              <TD bold right>{fmt(p.amount)}</TD>
              <TD><StatusBadge status={p.status} /></TD>
              <TD>
                {p.razorpay_order_id
                  ? <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{p.razorpay_order_id}</span>
                  : '—'}
              </TD>
              <TD>
                {p.expires_at
                  ? <span style={{ color: new Date(p.expires_at) > new Date() ? '#059669' : '#dc2626', fontWeight: 600 }}>
                      {new Date(p.expires_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  : '—'}
              </TD>
              <TD>{p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</TD>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

/* ── Listing Credits Table ── */
function CreditsTable({ data }) {
  const PLAN_LABELS = { single: '1 Credit', five: '5 Credits', fifteen: '15 Credits' };
  return (
    <TableWrap empty={data.length === 0}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <TH>#</TH>
            <TH>Plan</TH>
            <TH>Credits Granted</TH>
            <TH>Amount</TH>
            <TH>Status</TH>
            <TH>Razorpay Order</TH>
            <TH>Purchased On</TH>
          </tr>
        </thead>
        <tbody>
          {data.map((c, i) => (
            <tr key={c.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
              <TD>{i + 1}</TD>
              <TD bold>{PLAN_LABELS[c.plan_type] || c.plan_type}</TD>
              <TD>
                <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                  🏷️ {c.credits_granted} credits
                </span>
              </TD>
              <TD bold right>{fmt(c.amount)}</TD>
              <TD><StatusBadge status={c.status} /></TD>
              <TD>
                {c.razorpay_order_id
                  ? <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{c.razorpay_order_id}</span>
                  : '—'}
              </TD>
              <TD>{c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</TD>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

/* ── Car Bid Payments Table ── */
function BidsTable({ data }) {
  return (
    <TableWrap empty={data.length === 0}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <TH>#</TH>
            <TH>Car</TH>
            <TH>Listing</TH>
            <TH>Offer Price</TH>
            <TH>Bid Status</TH>
            <TH>Payment</TH>
            <TH>Date</TH>
          </tr>
        </thead>
        <tbody>
          {data.map((o, i) => (
            <tr key={o.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
              <TD>{i + 1}</TD>
              <TD bold>{o.car?.brand} {o.car?.model}</TD>
              <TD>
                <span style={{ fontSize: 12, color: '#0057b7', fontWeight: 600 }}>#{o.listing_id}</span>
              </TD>
              <TD bold right>{fmt(o.offer_price)}</TD>
              <TD>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                  background: o.status === 'accepted' ? '#dcfce7' : '#fee2e2',
                  color: o.status === 'accepted' ? '#166534' : '#991b1b',
                }}>
                  {o.status === 'accepted' ? '✅ Accepted' : o.status}
                </span>
              </TD>
              <TD>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                  background: (o.payment_status === 'success' || o.payment_status === 'paid') ? '#dcfce7' : '#fef9c3',
                  color: (o.payment_status === 'success' || o.payment_status === 'paid') ? '#166534' : '#854d0e',
                }}>
                  {(o.payment_status === 'success' || o.payment_status === 'paid') ? '✅ Paid' : '⏳ Pending'}
                </span>
              </TD>
              <TD>{o.created_at ? new Date(o.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</TD>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}